import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useRouter } from "next/navigation";
import ReCAPTCHAComponent from "react-google-recaptcha";
import { sendLoginUsername, sendLoginPasscode } from "@/dataLayer";
import { Merchant } from "@/dto/merchant";
import { getFromLocalStorage } from "@/utils/localStorageUtils";
import { LocalStorageKey } from "@/utils/storageKeys";
import { RegularModal } from "../modals/modals";
import { LoginModalUsernameInput } from "./loginModalUsernameInput";
import { LoginModalOTPInput } from "./loginModalOTPInput";

export type LoginFormFields = {
  countryCode: string;
  phone: string;
  formattedPhoneNumber: string;
  email: string;
};

export const LoginInfoModal = ({
  isOpen,
  onRequestClose,
  merchantColor,
  toCheckoutAfterLogin,
  merchant,
}: {
  merchant: Merchant;
  isOpen: boolean;
  onRequestClose: () => void;
  merchantColor: string;
  toCheckoutAfterLogin: boolean;
}) => {
  const router = useRouter();
  const [showVerification, setShowVerification] = useState<boolean>(false);
  const [showPhoneNumberInput, setShowPhoneNumberInput] =
    useState<boolean>(true);
  const [code, setCode] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const captchaRef = useRef<ReCAPTCHAComponent | null>(null);
  const recaptchaKey = process.env.NEXT_PUBLIC_RECAPTCHA_CONSUMER_WEB_KEY || "";
  const serviceCart = getFromLocalStorage(LocalStorageKey.SERVICE_CART, []);
  const showGuestCheckout =
    (merchant.isRetail || merchant.isRestaurant || serviceCart?.length === 0) &&
    toCheckoutAfterLogin;
  const phoneField = "phone";
  const emailField = "email";
  const showEmailSwitching = merchant?.enabledSettings?.includes(
    "EMAIL_SIGNUP_LOGIN_ON_COLO2"
  );

  useEffect(() => {
    if (code.length === 6) {
      setIsLoading(true);
      setErrorMessage(null);
      confirmCode();
    }
  }, [code]);

  useEffect(() => {
    if (isOpen && toCheckoutAfterLogin) {
      router.prefetch("/checkout");
    }
  }, [isOpen, toCheckoutAfterLogin]);

  const form = useForm({
    defaultValues: {
      countryCode: "",
      phone: "",
      formattedPhoneNumber: "",
      email: "",
    },
    mode: "all",
  });

  const {
    formState: { isValid },
    getValues,
    setValue,
  } = form;

  async function onRecaptchaSubmit(value: any) {
    if (!value) {
      return;
    }
    setIsLoading(true);
    let username = showPhoneNumberInput
      ? getValues("formattedPhoneNumber")
      : getValues("email");
    let recaptcha = value;
    const result = await sendLoginUsername(username, recaptcha);
    if (result.status === 204) {
      setShowVerification(true);
      setErrorMessage(null);
    } else {
      setErrorMessage(
        result.data.message || "An error occurred with the captcha."
      );
    }
    setIsLoading(false);
  }

  const confirmCode = async () => {
    let username = showPhoneNumberInput
      ? getValues("formattedPhoneNumber")
      : getValues("email");
    const result = await sendLoginPasscode(username, code);
    if (
      result?.globalCustomerUuid &&
      result?.refreshToken &&
      result?.sessionToken
    ) {
      setCode("");
      if (toCheckoutAfterLogin) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        router.push("/checkout");
      } else {
        setShowVerification(false);
        onRequestClose();
      }
    } else if (result.status && result.status.code === 403) {
      setErrorMessage(
        result.message || "There is an error with your OTP code."
      );
    } else {
      setErrorMessage("Unexpected error occurred. Please try again.");
    }
    setIsLoading(false);
  };

  const handleGuestCheckout = () => {
    if (toCheckoutAfterLogin) {
      router.push("/guest-checkout");
    } else {
      onRequestClose();
    }
  };

  return (
    <RegularModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      overlayClassName="bg-opacity-75"
    >
      <ReCAPTCHAComponent
        className=""
        sitekey={recaptchaKey}
        ref={captchaRef}
        onChange={onRecaptchaSubmit}
        size="invisible"
        // asyncScriptOnLoad={asyncScriptOnLoad}
        // onErrored={handleRecaptchaError}
        badge="bottomright"
      />
      {!showVerification ? (
        <LoginModalUsernameInput
          merchant={merchant}
          form={form}
          setValue={setValue}
          phoneField={phoneField}
          emailField={emailField}
          setErrorMessage={setErrorMessage}
          onRequestClose={onRequestClose}
          merchantColor={merchantColor}
          showPhoneNumberInput={showPhoneNumberInput}
          showEmailSwitching={showEmailSwitching}
          setShowPhoneNumberInput={setShowPhoneNumberInput}
          errorMessage={errorMessage}
          isLoading={isLoading}
          isValid={isValid}
          captchaRef={captchaRef}
          showGuestCheckout={showGuestCheckout}
          handleGuestCheckout={handleGuestCheckout}
        />
      ) : (
        <LoginModalOTPInput
          setValue={setValue}
          phoneField={phoneField}
          emailField={emailField}
          setErrorMessage={setErrorMessage}
          onRequestClose={onRequestClose}
          merchantColor={merchantColor}
          showPhoneNumberInput={showPhoneNumberInput}
          errorMessage={errorMessage}
          isLoading={isLoading}
          captchaRef={captchaRef}
          setShowVerification={setShowVerification}
          setCode={setCode}
          getValues={getValues}
          code={code}
        />
      )}
    </RegularModal>
  );
};
